import axiosInstance from './axios';
import { SignalResponse, SignalBatchUpdate, SignalBatchUpdateResponse, WeeklyHomeSnapshotResponse, WeeklySummaryResponse } from './types';

interface GetSignalsParams {
    business_id?: string;
    start_date: string;
    end_date: string;
    resolved?: 'open' | 'closed' | 'archived';
}

export interface SignalGenerationStatus {
    business_id: string;
    status: 'ready' | 'generating' | 'failed';
    created_at?: string | null;
    updated_at?: string | null;
    started_at?: string | null;
    completed_at?: string | null;
    last_generated_at?: string | null;
    error?: string | null;
}

export interface SignalStatusCounts {
    open: number;
    closed: number;
    archived: number;
}

export const signalApi = {
    /**
     * Get signals within a date range
     */
    getSignals: async ({ business_id, start_date, end_date, resolved }: GetSignalsParams): Promise<SignalResponse[]> => {
        const response = await axiosInstance.get('/signals/', {
            params: {
                business_id,
                start_date,
                end_date,
                resolved,
            }
        });
        return response.data;
    },

    getSignalGenerationStatus: async (business_id?: string): Promise<SignalGenerationStatus> => {
        const response = await axiosInstance.get('/signals/status', {
            params: { business_id }
        });
        return response.data;
    },

    getSignalCounts: async ({ business_id, start_date, end_date }: GetSignalsParams): Promise<SignalStatusCounts> => {
        const response = await axiosInstance.get('/signals/counts', {
            params: {
                business_id,
                start_date,
                end_date,
            }
        });
        return response.data;
    },

    getWeeklySummary: async (business_id?: string): Promise<WeeklySummaryResponse> => {
        const response = await axiosInstance.get('/signals/weekly-summary', {
            params: { business_id }
        });
        return response.data;
    },

    getHomeSnapshot: async (business_id?: string): Promise<WeeklyHomeSnapshotResponse> => {
        const response = await axiosInstance.get('/signals/home-snapshot', {
            params: { business_id }
        });
        return response.data;
    },

    dismissWeeklySummary: async (business_id?: string): Promise<WeeklySummaryResponse> => {
        const response = await axiosInstance.patch('/signals/weekly-summary/dismiss', null, {
            params: { business_id }
        });
        return response.data;
    },

    updateWeeklySummaryAdmin: async (payload: Partial<WeeklySummaryResponse> & { business_id: string }): Promise<WeeklySummaryResponse> => {
        const response = await axiosInstance.patch('/signals/weekly-summary/admin', payload);
        return response.data;
    },

    sendWeeklySummaryEmail: async (business_id: string, email?: string): Promise<{ status: string; message: string; recipient: string }> => {
        const response = await axiosInstance.post('/signals/weekly-summary/send', {
            business_id,
            email: email || null,
        });
        return response.data;
    },

    /**
     * Update a signal
     */
    updateSignal: async (
        id: string,
        updates: Partial<SignalResponse>,
        business_id?: string
    ): Promise<SignalResponse> => {
        const response = await axiosInstance.patch(`/signals/${id}`, {
            ...updates,
            business_id
        });
        return response.data;
    },

    /**
     * Delete a signal
     */
    deleteSignal: async (id: string): Promise<{ status: string; message: string }> => {
        const response = await axiosInstance.delete(`/signals/${id}`);
        return response.data;
    },

    /**
     * Trigger manual signal generation
     */
    generateSignals: async (business_id?: string): Promise<{ status: string, message: string }> => {
        const response = await axiosInstance.post('/signals/generate', null, {
            params: { business_id }
        });
        return response.data;
    },

    /**
     * Batch update signals
     */
    batchUpdateSignals: async (data: SignalBatchUpdate): Promise<SignalBatchUpdateResponse> => {
        const response = await axiosInstance.patch('/signals/batch', data, {
            params: data.business_id ? { business_id: data.business_id } : undefined,
        });
        return response.data;
    },

    /**
     * Admin: Create arbitrary signal
     */
    createAdminSignal: async (payload: any): Promise<SignalResponse> => {
        const response = await axiosInstance.post('/signals/admin', payload);
        return response.data;
    },

    /**
     * Admin: Update arbitrary signal completely
     */
    updateAdminSignal: async (id: string, payload: any): Promise<SignalResponse> => {
        const response = await axiosInstance.patch(`/signals/admin/${id}`, payload);
        return response.data;
    }
};

export default signalApi;
